// LGPD — direitos do titular. Quatro rotas que não compartilham prefixo
// (/v1/retention-policy, /v1/data-erasure-request, /v1/consent,
// /v1/data-subject/export) e ainda assim são um domínio só: todas respondem a
// UMA pergunta do encarregado — o que fazemos com o dado de uma pessoa, por
// quanto tempo, com que base legal e como devolvê-lo ou apagá-lo.
//
// A prova de que é um recorte e não uma gaveta: as três leituras alimentam a
// MESMA tela (Conformidade) e cada mutação derruba exatamente uma delas; nenhum
// outro domínio lê nada daqui, e daqui não se lê nada de ninguém.
//
// Todos estes endpoints usam a marca ativa no back; o front nunca envia um
// organizationId para evitar que a tela pareça capaz de atravessar tenants.
import { api, toApiError } from "./client.js";

export interface RetentionPolicy {
  recordingRetentionDays: number;
  transcriptionRetentionDays: number | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ErasureRequest {
  id: string;
  organizationId: string;
  requestedForLeadId: string;
  requestedBy: string;
  scope: string[];
  status: string;
  deadline: string;
  createdAt: string;
  startedAt: string | null;
  executedAt: string | null;
  error: string | null;
}

export interface ConsentRecord {
  id: string;
  organizationId: string;
  leadId: string | null;
  scope: "call_recording" | "call_transcription" | "crm_contact" | "marketing";
  legalBasis: string;
  textVersion: string | null;
  granted: boolean;
  evidence: unknown;
  createdAt: string;
  revokedAt: string | null;
}

export interface DataSubjectExport {
  lead: {
    id: string;
    hubspotLeadId: string | null;
    hubspotContactId: string | null;
    name: string | null;
    email: string | null;
    phone: string | null;
    source: string;
    createdAt: string;
    contactDeletedAt: string | null;
  };
  calls: Array<Record<string, unknown> & { id: string; recordingUrl: string | null }>;
  consents: ConsentRecord[];
}

export async function getRetentionPolicy(): Promise<RetentionPolicy> {
  const { data, error } = await api<{ policy: RetentionPolicy }>("/v1/retention-policy");
  if (error) throw toApiError("GET /v1/retention-policy", error);
  return data.policy;
}

export async function updateRetentionPolicy(input: {
  recordingRetentionDays: number;
  transcriptionRetentionDays: number | null;
}): Promise<RetentionPolicy> {
  const { data, error } = await api<{ policy: RetentionPolicy }>("/v1/retention-policy", {
    method: "PUT",
    body: input,
  });
  if (error) throw toApiError("PUT /v1/retention-policy", error);
  return data.policy;
}

export async function listErasureRequests(): Promise<ErasureRequest[]> {
  const { data, error } = await api<{ requests: ErasureRequest[] }>("/v1/data-erasure-request");
  if (error) throw toApiError("GET /v1/data-erasure-request", error);
  return data.requests;
}

export async function requestDataErasure(input: {
  leadId: string;
  scope?: string[];
}): Promise<ErasureRequest> {
  const { data, error } = await api<{ request: ErasureRequest }>("/v1/data-erasure-request", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/data-erasure-request", error);
  return data.request;
}

export async function listConsents(): Promise<ConsentRecord[]> {
  const { data, error } = await api<{ consents: ConsentRecord[] }>("/v1/consent");
  if (error) throw toApiError("GET /v1/consent", error);
  return data.consents;
}

export async function recordConsent(input: {
  leadId: string | null;
  scope: ConsentRecord["scope"];
  legalBasis: string;
  textVersion?: string;
  granted: boolean;
}): Promise<ConsentRecord> {
  const { data, error } = await api<{ consent: ConsentRecord }>("/v1/consent", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/consent", error);
  return data.consent;
}

export async function revokeConsent(id: string): Promise<void> {
  const path = `/v1/consent/${encodeURIComponent(id)}/revoke`;
  const { error } = await api(path, { method: "POST" });
  if (error) throw toApiError(`POST ${path}`, error);
}

export async function exportDataSubject(leadId: string): Promise<DataSubjectExport> {
  const { data, error } = await api<DataSubjectExport>("/v1/data-subject/export", {
    query: { lead_id: leadId },
  });
  if (error) throw toApiError("GET /v1/data-subject/export", error);
  return data;
}
